import React, { useState, useEffect } from "react";
import { View, StyleSheet, ScrollView, Dimensions } from "react-native";
import { useToast } from "../../../contexts/Toast/ToastProvider";
import { useForm, Controller } from "react-hook-form";
import CustomAdminModal from "../../../components/common/general/CustomAdminModal/CustomAdminModal";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import TextEditor from "../../TextEditor/TextEditor";
import { useAppConfig } from "../../../AppConfigProvider";
import {
  useCreateThreadMutation,
  useUpdateThreadMutation,
  useUpdatePostMutation,
} from "../../../graphql/generated/graphql";

interface DiscussionFormModalProps {
  visible: boolean;
  onClose: () => void;
  courseId: string;
  thread?: any;
}

interface FormData {
  title: string;
  content: string;
}

const DiscussionFormModal: React.FC<DiscussionFormModalProps> = ({ visible, onClose, courseId, thread }) => {
  const { primaryColor } = useAppConfig();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { showToast } = useToast();
  const isEditing = !!thread;
  const screenHeight = Dimensions.get("window").height;

  const [createThread] = useCreateThreadMutation();
  const [updateThread] = useUpdateThreadMutation();
  const [updatePost] = useUpdatePostMutation();

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      title: "",
      content: "",
    },
  });

  const watchTitle = watch("title");
  const watchContent = watch("content");

  useEffect(() => {
    if (visible && thread) {
      // For editing, populate with existing thread data
      const initialPost = thread.posts?.find((post: any) => post.depth === 0);
      reset({
        title: thread.title || "",
        content: initialPost?.message || "",
      });
    } else if (visible) {
      // For creating new thread, reset to empty
      reset({
        title: "",
        content: "",
      });
    }
  }, [visible, thread, reset]);

  const handleCancel = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: FormData) => {
    if (!data.title.trim() || !data.content.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditing) {
        // For editing, update both thread title and initial post content
        const initialPost = thread.posts?.find((post: any) => post.depth === 0);

        if (!initialPost) {
          throw new Error("Initial post not found");
        }

        // Update thread title
        const threadResult = await updateThread({
          variables: {
            threadId: thread.id,
            input: {
              title: data.title.trim(),
            },
          },
        });

        if (!threadResult.data?.updateThread?.success) {
          throw new Error(threadResult.data?.updateThread?.message || "Failed to update thread title");
        }

        // Update initial post content
        const postResult = await updatePost({
          variables: {
            postId: initialPost.id,
            input: {
              message: data.content.trim(),
            },
          },
        });

        if (!postResult.data?.updatePost?.success) {
          throw new Error(postResult.data?.updatePost?.message || "Failed to update discussion content");
        }
      } else {
        // Creating new thread
        const result = await createThread({
          variables: {
            input: {
              title: data.title.trim(),
              message: data.content.trim(),
              courseId: courseId,
            },
          },
        });

        if (!result.data?.createThread?.success) {
          throw new Error(result.data?.createThread?.message || "Failed to create thread");
        }
      }

      onClose();
    } catch (error) {
      console.error("Error submitting discussion:", error);
      // TODO: Add proper error handling/toast notification
      showToast({
        message: `Error ${isEditing ? "updating" : "creating"} discussion: ${error.message}`,
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderContent = () => (
    <>
      {/* Title Field */}
      <View style={styles.formGroup}>
        <CustomText
          text="Discussion Title"
          size="m"
          weight="bold"
          useTranslationText={false}
          style={styles.fieldLabel}
        />
        <CustomSpacing type="vertical" size="s" />
        <Controller
          control={control}
          name="title"
          rules={{
            required: "Discussion title is required",
            minLength: {
              value: 3,
              message: "Title must be at least 3 characters long",
            },
            maxLength: {
              value: 200,
              message: "Title must be less than 200 characters",
            },
          }}
          render={({ field: { onChange, value } }) => (
            <CustomTextInput
              value={value}
              onChangeText={onChange}
              placeholder="Enter discussion title..."
              style={[styles.titleInput, errors.title && styles.inputError]}
            />
          )}
        />
        {errors.title && (
          <CustomText text={errors.title.message || ""} size="s" useTranslationText={false} style={styles.errorText} />
        )}
      </View>

      <CustomSpacing type="vertical" size="l" />

      {/* Content Field */}
      <View style={styles.formGroup}>
        <CustomText
          text="Discussion Content"
          size="m"
          weight="bold"
          useTranslationText={false}
          style={styles.fieldLabel}
        />
        <CustomSpacing type="vertical" size="s" />
        <Controller
          control={control}
          name="content"
          rules={{
            required: "Discussion content is required",
            minLength: {
              value: 10,
              message: "Content must be at least 10 characters long",
            },
          }}
          render={({ field: { onChange, value } }) => (
            <View style={[styles.editorContainer, errors.content && styles.inputError]}>
              <TextEditor value={value} onChange={onChange} placeholder="Write your discussion content here..." />
            </View>
          )}
        />
        {errors.content && (
          <CustomText
            text={errors.content.message || ""}
            size="s"
            useTranslationText={false}
            style={styles.errorText}
          />
        )}
      </View>
    </>
  );

  return (
    <CustomAdminModal
      visible={visible}
      onRequestClose={handleCancel}
      ariaLabel={isEditing ? "Edit Discussion Modal" : "Create Discussion Modal"}
      title={isEditing ? "Edit Discussion" : "Create New Discussion"}
      style={styles.modal}>
      <View style={styles.container}>
        <View style={styles.content}>
          <ScrollView
            style={[styles.scrollContent, { maxHeight: screenHeight * 0.7 }]}
            showsVerticalScrollIndicator={true}
            contentContainerStyle={styles.scrollContentContainer}>
            {renderContent()}
          </ScrollView>

          <View style={styles.buttonContainer}>
            <View style={styles.buttonWrapper}>
              <CustomButton
                text="Cancel"
                onPress={handleCancel}
                styleType="primaryLight"
                size="m"
                disabled={isSubmitting}
                style={styles.cancelButton}
                bold={true}
              />
              <CustomButton
                text={
                  isSubmitting
                    ? isEditing
                      ? "Updating..."
                      : "Creating..."
                    : isEditing
                      ? "Update Discussion"
                      : "Create Discussion"
                }
                onPress={handleSubmit(onSubmit)}
                styleType="primary"
                size="m"
                disabled={isSubmitting || !watchTitle.trim() || !watchContent.trim()}
                style={[
                  styles.submitButton,
                  {
                    backgroundColor:
                      isSubmitting || !watchTitle.trim() || !watchContent.trim() ? "#E5E7EB" : primaryColor,
                  },
                ]}
                textStyle={{
                  color: isSubmitting || !watchTitle.trim() || !watchContent.trim() ? "#6B7280" : "#FFFFFF",
                }}
                bold={true}
              />
            </View>
          </View>
        </View>
      </View>
    </CustomAdminModal>
  );
};

const styles = StyleSheet.create({
  modal: {
    width: "90%",
    maxWidth: 600,
    padding: 0,
    alignSelf: "center",
  },
  container: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    overflow: "hidden",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    minHeight: "auto",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
  },
  scrollContent: {
    flex: 1,
    paddingHorizontal: 24,
    paddingTop: 24,
  },
  scrollContentContainer: {
    paddingBottom: 24,
  },
  formGroup: {
    width: "100%",
  },
  fieldLabel: {
    color: "#111827",
  },
  titleInput: {
    backgroundColor: "#F8F8F8",
    borderColor: "#D1D5DB",
    borderRadius: 10,
    fontSize: 14,
  },
  editorContainer: {
    borderRadius: 4,
    overflow: "hidden",
    minHeight: 200,
    borderWidth: 1,
    borderColor: "#D1D5DB",
  },
  inputError: {
    borderColor: "#EF4444",
  },
  errorText: {
    color: "#EF4444",
    marginTop: 4,
  },
  buttonContainer: {
    backgroundColor: "#FFFFFF",
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    paddingHorizontal: 24,
    paddingVertical: 16,
    flexShrink: 0,
  },
  buttonWrapper: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  cancelButton: {
    backgroundColor: "transparent",
    borderWidth: 2,
    borderColor: "#3E68FE",
    borderRadius: 8,
    paddingHorizontal: 16,
  },
  submitButton: {
    borderRadius: 8,
    paddingHorizontal: 24,
  },
});

export default DiscussionFormModal;
